<template>
  <div class="hardware-faults">
    <v-tabs v-model="tab" align-with-title>
      <v-tabs-slider color="secondary"></v-tabs-slider>
			<v-tab v-for="page in faultPages" :key="page">
        {{ page }}
      </v-tab>
    </v-tabs>
    <v-row align="center device-control-bar">
      <v-col 
        v-if="displayTable('Per - Org')" 
        cols="2"
      >
        <v-autocomplete
          v-model="selectedOrg"
          :items="orgName"
          label="Organisations"
          item-text="name"
          outlined
          @change="getFaultsByOrg"
        />
      </v-col>

      <v-col 
        v-if="displayTable('Daily (All Orgs)')" 
        cols="3"
      >
        <material-date-time-range 
          :start-value="startValue" 
          :local-time="true"
          :current-date-only="true"
          @singleDateSelected="date => selectedSingleDate = date"
        >
        </material-date-time-range>
      </v-col>

      <v-card-title class="faults-search-bar">
        <div style="display: flex; align-items: center">
          <div style="width: 250px">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </div>

          <div 
            style="margin-left: 10px"
          >
            <v-chip-group v-model="selectedFilter" multiple active-class="third--text" class="ma-4">
              <v-chip 
                v-for="tag in filters" 
                :key="tag"
              >
                {{ filterMap(tag) }}
              </v-chip>
            </v-chip-group>
          </div>

          <TableRefresh 
            :state="loading"
            @click.native="refreshWrapper"
          />

          <div class="download-container"> 
            <v-icon class="download-icon" @click="downloadFile">
              mdi-download
            </v-icon>
          </div>
        </div>
      </v-card-title>
    </v-row>

    
    <v-data-table
      v-if="displayTable('Per - Org')"
      :headers="headers.org"
      :items="filteredItems"
      :loading="loading"
      :search="search"
      :footer-props="{
        itemsPerPageOptions:[25,50,100,-1]
      }"
      :items-per-page="-1"
      class="elevation-1"
    >
 
        <template v-slot:progress>
          <v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
        </template>

        <template v-slot:item.name="{ item }">{{ item.vehicle_id }}</template>

        <template v-slot:item.storage="{ item }">
          <v-icon v-if="faults.storage.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
        </template>

        <template v-slot:item.camera="{ item }">
          <v-icon v-if="faults.camera.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
        </template>

        <template v-slot:item.recording="{ item }">
          <v-icon v-if="faults.recording.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
        </template>

        <template v-slot:item.gps="{ item }">
          <v-icon v-if="faults.gps.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
        </template>

        <template v-slot:item.network="{ item }">
          <v-icon v-if="faults.status.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
        </template>

        <template v-slot:item.desc="{ item }">{{faultDescription(item.faults)}}</template>
    </v-data-table>

    <v-data-table
      v-if="displayTable('Daily (All Orgs)')"
      :headers="headers.daily"
      :items="filteredDailyReport"
      :loading="loading"
      :search="search"
      :footer-props="{
        itemsPerPageOptions:[25,50,100,-1]
      }"
      :sort-by.sync="sortBy"
      :sort-desc.sync="descending"
      :items-per-page="-1"
      class="elevation-1"
    >
      <template v-slot:progress>
        <v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
      </template>

      <!-- <template v-slot:item.storage="{ item }">
        <v-icon v-if="dailyReportFaults.storage.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template> -->

      <template v-slot:item.storage="{ item }">
        <v-icon v-if="dailyReportFaults.storage.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template>

      <template v-slot:item.camera="{ item }">
        <v-icon v-if="dailyReportFaults.camera.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template>

      <template v-slot:item.recording="{ item }">
        <v-icon v-if="dailyReportFaults.recording.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template>

      <template v-slot:item.gps="{ item }">
        <v-icon v-if="dailyReportFaults.gps.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template>

      <template v-slot:item.network="{ item }">
        <v-icon v-if="dailyReportFaults.status.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template>

      <template v-slot:item.desc="{ item }">{{faultDescription(item.faults)}}</template>

    </v-data-table>
  </div>
</template>

<script>

import { Logger } from "aws-amplify"
const logger = new Logger("Org Faults")

import { ListAccessibleOrgs, GetOrgFaults, GetFaultReport } from '@/services/API.js';

import moment from "moment";
import xlsx from 'xlsx';

import TableRefresh from '@/components/general/TableRefresh.vue';

export default {
  name: 'Faults',
  components: {
    TableRefresh
  },
  data()  {
    return {
      tab: 0,
      loading: false,
      search: '',
      selectedOrg: '',
      orgs: [],
      faults: [],
      vehicles: [],
      errors: null,
      filters: [
        "storage", 
        "camera", 
        "recording",
        "gps", 
        "status"
      ],
      selectedFilter: [],
      headers: {
        org: [
          { text: "Vehicle", value: "vehicle_name", sortable: false },
          { text: "Storage", value: "storage", align: "center", sortable: false },
          { text: "Video Loss", value: "camera", align: "center", sortable: false },
          { text: "Not Recording", value: "recording", align: "center", sortable: false },
          { text: "GPS", value: "gps", align: "center", sortable: false },
          { text: "Offline 7+ Days", value: "network", align: "center", sortable: false },
          { text: "Description", value: "desc", sortable: false },
        ],
        daily: [
          { text: "Created On", value: "created_on", sortable: true },
          { text: "Organisation", value: "org_name", sortable: true },
          { text: "Vehicle", value: "vehicle_name", sortable: true },
          { text: "Storage", value: "storage", align: "center", sortable: false },
          { text: "Video Loss", value: "camera", align: "center", sortable: false },
          { text: "Not Recording", value: "recording", align: "center", sortable: false },
          { text: "GPS", value: "gps", align: "center", sortable: false },
          { text: "Offline 7+ Days", value: "network", align: "center", sortable: false },
          { text: "Description", value: "desc", sortable: false },
        ]
      },
      initFaults: {
        camera: {
          count: 0,
          vehicles: new Set()
        },
        storage: {
            count: 0,
            vehicles: new Set()
        },
        gps: {
            count: 0,
            vehicles: new Set()
        },
        network:  {
            count: 0,
            vehicles: new Set()
        },
        recording: {
            count: 0,
            vehicles: new Set()
        },
        status: {
          count: 0,
          vehicles: new Set()
        }
      },
      faultPages: [
        'Per - Org',
        'Daily (All Orgs)'
      ],
      selectedDates: [
        moment().startOf("day").format("YYYY-MM-DD"),
        moment().endOf("day").format("YYYY-MM-DD")
      ],
      startValue: [
        moment().startOf("day").format("YYYY-MM-DD"),
        moment().endOf("day").format("YYYY-MM-DD")
      ],
      selectedSingleDate: '',
      dailyReports: [],
      reportItems: [],
      dailyReportFaults: [],
      sortBy: 'org_name',
      descending: false
    }
  },
  computed: {
    formatSelectedDates() {
      return [
        moment(this.selectedDates[0]).format("YYYY-MM-DD"),
        moment(this.selectedDates[1]).format("YYYY-MM-DD")
      ]
    },
    orgName() {
      const orgs = this.orgs.map((org) => ({
        name: org.name
      }));

      return orgs;
    },
    filteredItems() {
      const keywords = this.selectedFilter.map(index => this.filters[index]);
      let results = [];

      for(const word in keywords) {
        var filter = keywords[word];
        
        for (const i in this.vehicles) {
          var vehicle = this.vehicles[i];
          var faults = vehicle.faults;

          for (const j in faults) {
            let fault = faults[j];

            if (fault.type.toLowerCase() == filter) {
              let index = results.indexOf(vehicle);

              if (index > -1) {
                // results.splice(index, 1);
              } else {
                results.push(vehicle);
              }
              
            }
          }
      }
    }

    if (keywords.length > 0 ) {
      return results;
    }
    else {
      return this.vehicles;
    }

    },

    filteredDailyReport() {
      const keywords = this.selectedFilter.map(index => this.filters[index]);
      let results = [];

      for(const word in keywords) {
        var filter = keywords[word];
        
        for (const i in this.reportItems) {
          var vehicle = this.reportItems[i];
          var faults = vehicle.faults;

          for (const j in faults) {
            let fault = faults[j];
            logger.info('debug ', fault.type, filter)
            if (fault.type.toLowerCase() == filter) {
              let index = results.indexOf(vehicle);

              if (index > -1) {
                // results.splice(index, 1);
              } else {
                results.push(vehicle);
              }
              
            }
          }
      }
    }

    if (keywords.length > 0 ) {
      return results.sort((a, b) => a.org_name.localeCompare(b.org_name));
    }
    else {
      return this.reportItems;
    }

    },
    selectedPage() {
      switch (this.tab) {
        case 0:
          return "Per - Org";
        case 1:
          return "Daily (All Orgs)";
        default:
          return "Per - Org";
      } 
    }
  },
  watch: {
    tab(val) {
      if(val == 1) {
        this.getDailyFaultsReport()
      }
    },
    selectedSingleDate() {
      this.selectedDates = [
        moment(this.selectedSingleDate).startOf("day").format("YYYY-MM-DD"),
        moment(this.selectedSingleDate).endOf("day").format("YYYY-MM-DD")
      ];
      this.getDailyFaultsReport();
    }
  },
  mounted() {
    this.getAllOrgs();
  },
  methods: {
    testFocus(e) {
      logger.info('testing focus ', e)
    },  
    displayTable(table) {
      return this.selectedPage == table ? true : false
    },
    getAllOrgs() {
      ListAccessibleOrgs()
      .then(res => {
        this.orgs = res.sort((a, b) => a.name.localeCompare(b.name));
      })
      .catch(err => {
        this.errors = err;
      });
    },
    retrunOrgID(name) {
      let org = this.orgs.find(org => {
        return org.name == name;
      });

      return org.id;
    },
    getFaultsByOrg() {
      this.devices = [];

      if (this.selectedOrg.length > 0) {
        this.loading = true;

        let id = this.retrunOrgID(this.selectedOrg);

        if (id) {
          GetOrgFaults(id)
          .then(res => {
            if (res.Faults) {
              this.vehicles = res.Faults.filter(fa => fa.faults.length > 0);
              this.faults = this.convertFaultsCount(res.Faults);

              this.loading = false;
            }
          })
          .catch(err => {
            logger.info(err);
            this.loading = true;
          })
        }
      }
    },
    faultDescription(faults) {
      let text = [];
      let faultObjs = this.convertFaultsToString(faults);
      
      faultObjs.forEach(fault => {
        text.push(fault.text);
      });

      return text.join("; ");
    },
    faultsMap(fault) {
      let map = {
          'dirty bit set': 'Disk possibly corrupted'
      }

      // eslint-disable-next-line no-prototype-builtins
      return map.hasOwnProperty(fault) ?  map[fault] : fault;
    },
    filterMap(filter) {
      let map = {
        storage: 'Storage',
        camera: 'Video Loss',
        recording: 'Not Recording',
        gps: 'GPS',
        status: 'Offline 7+ Days'
      }

      return map[filter];
    },
    splitCamelCase(str) {
      const res = str
        .split(/(?=[A-Z])/)
        .map(char => char.toLowerCase())
        .join(" ");

      return res.charAt(0).toUpperCase() + res.slice(1);
    },
    convertFaultsToString(faults) {
      const res = new Map()
      faults.forEach(fault => {
          let temp = null
          let key = `${fault.type}-${fault.status}`
          if (res.has(key)) temp = res.get(key)

          let value = temp ? temp.concat(fault.context) :
              [fault.context]
          res.set(key, value)
      })

      const result = []
      res.forEach((value, key) => {
          let fault = key.split('-')
          let faultDetails = {
              type: '',
              text: ``
          } 
          
          switch (fault[0].toLowerCase()) {
              case 'storage': {
                  const disks = value.map(val => `${val.medium} ${val.id}`)

                  faultDetails.type = 'storage';
                  faultDetails.text = `${disks.join()} ${this.faultsMap(fault[1])}`;

                  result.push(faultDetails);
                  break;
              }

              case 'camera': {
                  faultDetails.type = 'camera';
                  faultDetails.text = `${this.splitCamelCase(fault[1])} at channel ${value.map(val => val.channel).join()}`;

                  result.push(faultDetails);
                  break;
              }
              case 'gps':
                  faultDetails.type = 'gps';
                  faultDetails.text = 'No GPS signal';

                  result.push(faultDetails);
                  break;

              case 'GPS':
                  faultDetails.type = 'gps';
                  faultDetails.text = 'No GPS signal';

                  result.push(faultDetails);
                  break;

              case 'network':
                  faultDetails.type = 'network';
                  faultDetails.text = 'No network';

                  result.push(faultDetails);
                  break;

              case 'recording':
                  faultDetails.type = 'recording';
                  faultDetails.text = `Not Recording at channel ${value.map(val => val.channel).join()}`;

                  result.push(faultDetails);
                  break;
              case 'status':
                  faultDetails.type = 'status';
                  faultDetails.text = `Offline 7+ days`;

                  result.push(faultDetails);
                  break;
              default:
                  break;
          }
      })
      return result
    },
    convertFaultsCount(faults) {
      const response = faults.reduce((res, fa) => {
          if (fa.faults.length === 0) return res
          let target = fa.faults[0]

          if (fa.faults[0].type) {
              target = fa.faults.reduce((res, fa) => {
                // The daily report returns GPS as all uppercase so I have to make sure it's lower case here so that both org and daily report filtering works
                fa.type = fa.type.toLowerCase()
                  if (Number.isInteger(res[fa.type])) res[fa.type]++
                  res[fa.type] = 1
                  return res
              }, {})
          }
         
          Object.keys(target).forEach(name => {
              let fixedName = name
          
              if(res[fixedName]) {
            
                res[fixedName].count += target[fixedName]
                res[fixedName].vehicles.add(fa.vehicle_id)
              }
              else {
                logger.info('error : ', fixedName, res)
              }
        
          })
          return res
      }, this.initFaults);

      return response;
    },
    checkForFault(faults, type) {
      let fault = faults.find(fault => {
        return fault.type == type
      });

      return fault ? 'FAULT' : 'OK';
    },
    formatCSV(data, org = false) {
      logger.info('format CSV ', data)
      // Vehicle,Last Report Date,Last Report Time,GPS,Storage,Video Signal,Video Recording,Network,Info
      let mappedData = data.map(item => ({
        ...(org && {Organisation : item.org_name}),
        ['Vehicle']: item.vehicle_name,
        ['GPS']: this.checkForFault(item.faults, 'gps'),
        ['Storage']: this.checkForFault(item.faults, 'storage'),
        ['Video Signal']: this.checkForFault(item.faults, 'camera'),
        ['Video Recording']: this.checkForFault(item.faults, 'camera'),
        ['Network']: this.checkForFault(item.faults, 'status'),
        ['Info']: this.formatFaultsInfo(item.faults)
      }));

      return mappedData;
    },
    formatFaultsInfo(faults) {
      let text = '';
      let faultObjs = this.convertFaultsToString(faults);
      
      faultObjs.forEach(fault => {
        let faultString = (`[${fault.text}] `).toString();

        text += faultString;
      });

      return text;
    },
    downloadFile () {
      if (this.tab == 0) {
        this.createOrgReportFile();
      }
      else if (this.tab == 1) {
        this.createDailyReportFile();
      }
    },
    getDailyFaultsReport() {
      this.loading = true;
      this.reportItems = [];

      GetFaultReport('daily_fault', this.formatSelectedDates)
      .then(res => {
        this.loading = false;
        this.dailyReports = res;

        return res
      })
      .then(res => {
        let reports = [];
  
        res.forEach(item => {
          reports.push({
            url: item.s3_link,
            created_on: item.created_on
          })
        });

        reports.forEach(report => {
          this.prepareReports(report.url, report.created_on)
        });
      })
      .catch(err => {
        this.loading = false;
        logger.info('there was an error : ', err);
      })
    },
    prepareReports(url, date) {
      this.parseCSV(url)
      .then(item => {
        item.forEach(fault => {
          let data = fault.faults.length > 0 ? fault.faults.replace(/'/g, '"') : "[]";
          let jsonFaults = JSON.parse(data);
          logger.info('JSON fault', jsonFaults)
          fault.created_on = moment(date).format('YYYY-MM-DD');
          fault.faults = jsonFaults;
          fault.faultString = this.faultDescription(jsonFaults);
          this.reportItems.push(fault);
        });

        this.dailyReportFaults = this.convertFaultsCount(this.reportItems);
      })
      .catch(err => {
        logger.info('There was an error parsing the CSV ', err)
      })
    },
    createOrgReportFile() {
      if (this.filteredItems.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new();
        
        const formatData = this.formatCSV(this.vehicles);
        // create our worksheets
        const report = xlsx.utils.json_to_sheet(formatData);

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, report, this.reportTitle);

        const reportName = `${this.selectedOrg}_faults_${moment().format("YYYY-MM-DD HH:mm:ss")}`;
        xlsx.writeFile(wb, reportName + ".xlsx");
      }
    },
    createDailyReportFile() {
      // create our excel file
      const wb = xlsx.utils.book_new();
      
      const formatData = this.formatCSV(this.reportItems, true);
      // create our worksheets
      const report = xlsx.utils.json_to_sheet(formatData);

      // apend them to the workbook
      xlsx.utils.book_append_sheet(wb, report, this.reportTitle);

      const reportName = `daily_faults_report_${moment().format("YYYY-MM-DD HH:mm:ss")}`;
      xlsx.writeFile(wb, reportName + ".xlsx");
    },
    parseCSV(data) {
      let parsedData; 

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (res, reject) => {
        this.$papa.parse(data, {
          download: true,
          header: true,
          skipEmptyLines: true,
          delimiter: ",",
          complete: (results) => {
            parsedData = results.data;
            
            res(parsedData);
          },
          error: (error) => {
            reject(error);
          }
        });
      });
    },
    refreshWrapper() {
      if(this.tab == 0) {
        this.getFaultsByOrg()
      }
      else if (this.tab == 1) {
        this.getDailyFaultsReport();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .hardware-faults {
    padding: 1em;
     border: 2px solid rgb(196, 196, 196);
    border-bottom: 0;
    position: relative;
    height: calc(100vh - 64px);
    overflow-y: scroll;
    overflow-x: visible; /* Hide horizontal scrollbar */

    &::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #aaa;
      transition: all .2s;

      &:hover {
        background: #999;
      }
    }

    .faults-search-bar {
      padding-bottom: 2.5em;
    }

    .refresh-icon {
      top: inherit !important;
    }
    .download-container {
      position: absolute;
      right: 45px;
      top: inherit !important;
    }
  }
  
</style>